/**
 * Lectura de la primera hoja de un Excel (.xlsx) o de un CSV en el navegador, sin librerías externas:
 * el .xlsx es un ZIP con XML; se descomprime con DecompressionStream («deflate-raw»).
 */
import { leerCSV } from "../../dominio/src/index.ts";

const u16 = (b: Uint8Array, i: number) => b[i] | (b[i + 1] << 8);
const u32 = (b: Uint8Array, i: number) => (b[i] | (b[i + 1] << 8) | (b[i + 2] << 16) | (b[i + 3] << 24)) >>> 0;

async function inflar(datos: Uint8Array): Promise<Uint8Array> {
  const flujo = new Blob([datos as BlobPart]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
  return new Uint8Array(await new Response(flujo).arrayBuffer());
}

/** Archivos de un ZIP (solo los que se piden, para no descomprimir imágenes u otros adjuntos). */
async function leerZip(b: Uint8Array, quiero: (nombre: string) => boolean): Promise<Map<string, string>> {
  let fin = -1;
  for (let i = b.length - 22; i >= Math.max(0, b.length - 65557); i--) if (u32(b, i) === 0x06054b50) { fin = i; break; }
  if (fin < 0) throw new Error("El archivo no es un Excel válido (.xlsx).");
  const total = u16(b, fin + 10);
  let p = u32(b, fin + 16);
  const out = new Map<string, string>();
  const texto = new TextDecoder("utf-8");
  for (let k = 0; k < total; k++) {
    if (u32(b, p) !== 0x02014b50) throw new Error("El Excel está dañado.");
    const metodo = u16(b, p + 10), comprimido = u32(b, p + 20), largoNombre = u16(b, p + 28), extra = u16(b, p + 30), comentario = u16(b, p + 32), local = u32(b, p + 42);
    const nombre = texto.decode(b.subarray(p + 46, p + 46 + largoNombre));
    p += 46 + largoNombre + extra + comentario;
    if (!quiero(nombre)) continue;
    const ini = local + 30 + u16(b, local + 26) + u16(b, local + 28);
    const crudo = b.subarray(ini, ini + comprimido);
    const datos = metodo === 0 ? crudo : metodo === 8 ? await inflar(crudo) : null;
    if (!datos) throw new Error("El Excel usa una compresión que Donna no reconoce: guárdalo de nuevo como .xlsx o como CSV.");
    out.set(nombre, texto.decode(datos));
  }
  return out;
}

const xml = (s: string) => new DOMParser().parseFromString(s, "application/xml");
const columna = (ref: string) => { let n = 0; for (const ch of ref.replace(/\d+$/, "")) n = n * 26 + (ch.charCodeAt(0) - 64); return n - 1; };

export async function leerXLSX(buf: ArrayBuffer): Promise<string[][]> {
  const b = new Uint8Array(buf);
  const archivos = await leerZip(b, (n) => /^xl\/(workbook\.xml|_rels\/workbook\.xml\.rels|sharedStrings\.xml|worksheets\/[^/]+\.xml)$/.test(n));
  // primera hoja según el libro (no siempre se llama sheet1.xml)
  let ruta = "";
  const libro = archivos.get("xl/workbook.xml"), rels = archivos.get("xl/_rels/workbook.xml.rels");
  if (libro && rels) {
    const hoja = xml(libro).getElementsByTagName("sheet")[0];
    const rid = hoja?.getAttribute("r:id") ?? hoja?.getAttributeNS("http://schemas.openxmlformats.org/officeDocument/2006/relationships", "id");
    const rel = [...xml(rels).getElementsByTagName("Relationship")].find((r) => r.getAttribute("Id") === rid);
    const t = rel?.getAttribute("Target") ?? "";
    ruta = t.startsWith("/") ? t.slice(1) : `xl/${t.replace(/^\.\//, "")}`;
  }
  if (!archivos.has(ruta)) ruta = [...archivos.keys()].filter((k) => k.startsWith("xl/worksheets/")).sort()[0] ?? "";
  const hojaXml = archivos.get(ruta);
  if (!hojaXml) throw new Error("No encontré ninguna hoja en el Excel.");
  const compartidos = [...xml(archivos.get("xl/sharedStrings.xml") ?? "<sst/>").getElementsByTagName("si")]
    .map((si) => [...si.getElementsByTagName("t")].map((t) => t.textContent ?? "").join(""));
  const filas: string[][] = [];
  for (const fila of xml(hojaXml).getElementsByTagName("row")) {
    const r = Number(fila.getAttribute("r") ?? filas.length + 1) - 1;
    const celdas: string[] = [];
    let siguiente = 0;
    for (const c of fila.getElementsByTagName("c")) {
      const ref = c.getAttribute("r");
      const col = ref ? columna(ref) : siguiente;
      siguiente = col + 1;
      const tipo = c.getAttribute("t");
      const v = c.getElementsByTagName("v")[0]?.textContent ?? "";
      let valor = v;
      if (tipo === "s") valor = compartidos[Number(v)] ?? "";
      else if (tipo === "inlineStr") valor = [...c.getElementsByTagName("t")].map((t) => t.textContent ?? "").join("");
      else if (tipo === "b") valor = v === "1" ? "Sí" : "No";
      else if (!tipo || tipo === "n") valor = /^-?\d+(\.\d+)?(E[+-]?\d+)?$/i.test(v) && /E/i.test(v) ? String(Number(v)) : v;
      celdas[col] = valor;
    }
    filas[r] = Array.from(celdas, (x) => x ?? "");
  }
  return Array.from(filas, (f) => f ?? []).filter((f) => f.some((c) => String(c).trim() !== ""));
}

/** CSV en UTF-8 o en la codificación de Excel en Windows (Windows-1252). */
export function decodificarCSV(buf: ArrayBuffer): string {
  try { return new TextDecoder("utf-8", { fatal: true }).decode(buf); }
  catch { return new TextDecoder("windows-1252").decode(buf); }
}

export async function leerHoja(archivo: File): Promise<string[][]> {
  const buf = await archivo.arrayBuffer();
  const b = new Uint8Array(buf, 0, Math.min(4, buf.byteLength));
  if (b[0] === 0x50 && b[1] === 0x4b) return leerXLSX(buf);
  if (b[0] === 0xd0 && b[1] === 0xcf) throw new Error("Es un Excel antiguo (.xls): ábrelo en Excel y guárdalo como .xlsx o CSV.");
  return leerCSV(decodificarCSV(buf));
}
